/**
 * Sheet notes — the firm's per-sheet-type notes (Supabase `sheet_notes`, database/294,
 * api/sheet-notes.js). AlderCAD prints a sheet type's blocks in the title strip's notes band
 * (bim/sheets/sheetNotesBand.js, docs/bim-plan/28 round 10); Templates -> Sheet Notes edits them.
 */

async function post(payload) {
  const headers = { 'Content-Type': 'application/json' }
  if (typeof window !== 'undefined' && typeof window.__clerkGetToken !== 'function') {
    for (let i = 0; i < 40 && typeof window.__clerkGetToken !== 'function'; i++) {
      await new Promise((r) => setTimeout(r, 250))
    }
  }
  if (typeof window !== 'undefined' && typeof window.__clerkGetToken === 'function') {
    try { const t = await window.__clerkGetToken(); if (t) headers['Authorization'] = 'Bearer ' + t } catch { /* unauthed */ }
  }
  const res = await fetch('/api/sheet-notes', { method: 'POST', headers, body: JSON.stringify(payload) })
  const data = await res.json().catch(() => ({ error: 'Bad response from /api/sheet-notes' }))
  if (!res.ok || data.error) throw new Error(data.error || ('Request failed (' + res.status + ')'))
  return data
}

/** every block, by sheet type then order: `{ id, templateId, title, body, appliesWhen, active }` */
export async function listSheetNotes() {
  const { rows } = await post({ action: 'list' })
  return rows || []
}

/** change a block's heading, notes (one per line), applicability and/or whether it prints */
export async function saveSheetNote(id, patch, byName) {
  const { row } = await post({ action: 'save', id, ...patch, byName })
  return row
}

export async function addSheetNote(templateId, title, byName) {
  const { row } = await post({ action: 'add', templateId, title, byName })
  return row
}

export async function removeSheetNote(id) {
  await post({ action: 'remove', id })
}

/** swap a block with its neighbour within its sheet type (dir -1 = up, 1 = down) */
export async function moveSheetNote(id, dir) {
  await post({ action: 'move', id, dir })
}

/**
 * The firm's own sheet types (Drawing Sheet Templates, Airtable tblNWHEYC83Qcc2zX) — the ones
 * AlderCAD draws, so the ones that can carry notes. Consultant types are left out.
 */
export const SHEET_NOTE_TYPES = [
  { id: 'recxxt59RZSVUCsfN', name: 'A001 Site Plan' },
  { id: 'recHmPrDSQXeRrZtr', name: 'A00X Enlarged Site Plans' },
  { id: 'reckufUCfVmL97Es6', name: 'A00X Site Details' },
  { id: 'rec731KR7RB0CCMIg', name: 'A011 Door & Window Schedules' },
  { id: 'recNbdZMRKs4rZ4Eu', name: 'A100 Sub-Floor Plan' },
  { id: 'recZgHEf9Ne5u1q2u', name: 'A10X Floor Plans' },
  { id: 'rec3CXB0ewNj21v1Y', name: 'A12X Dimension Plans' },
  { id: 'recTDmSAAQQtsLACJ', name: 'A13X Reflected Ceiling Plans' },
  { id: 'recdms5dY7MKztKnU', name: 'A14X Roof Plans' },
  { id: 'recZvisH6joCHbVW6', name: 'A20X Building Sections' },
  { id: 'recN1VJ2HlxnYoljx', name: 'A21X Exterior Elevations' },
  { id: 'recK4beKRD26xwdEi', name: 'A30X Wall Sections' },
  { id: 'rec42FtDKdnoQsWTg', name: 'A40X Enlarged Plans' },
  { id: 'recLWj8ycw7nuJPLg', name: 'D10X Demolition Floor Plan' },
  { id: 'rec78DWFozgwxMZqR', name: 'D14X Demolition Roof Plan' },
  { id: 'recJpBTnT8C0lopjE', name: 'D20X Demolition Exterior Elevations' },
  { id: 'recpUGQMt9HxP9feW', name: 'AB10X Existing Floor Plans' },
  { id: 'rectskCfJRtR8a9eG', name: 'AB14X Existing Roof Plan' },
  { id: 'rec3JUDh9l2g5YSFx', name: 'AB21X Existing Exterior Elevations' },
  { id: 'recSjTHUNeWNQdHG0', name: 'FDSAP Fire Department Site Access Plan' },
  { id: 'recwylXEykgBFbX9D', name: 'G00X Area Plans' },
  { id: 'recK4hokHOaa8NZqu', name: 'G00X Story Pole Plan' },
  { id: 'recdVbfZzM0rvlZSr', name: 'G00X Open Space Plan' },
  { id: 'recr4HP6eMng6E60P', name: 'G10X Code Analysis Plan' },
  { id: 'recMUj5dvlS3nU1rL', name: 'EXT02 Exterior Elevations' },
  { id: 'rectApqTAn26Auq50', name: 'EXT0X Exterior Door and Window Schedules' },
  { id: 'recZekAHRKV23v46T', name: 'INT0X Reflected Ceiling Plan' },
  { id: 'rechAtPhS4Q4yH4w5', name: 'INT0X Interior Flooring Plan' },
  { id: 'rec2Ah0k0XkmYJfsr', name: 'INT0X Interior Elevations' },
]
